import request from '../../../components/Infrastructure/Requests/Request';
import pubsub from 'pubsub-js';
import {UI_EVENT} from '../../../components/Shared/Constants/Events';
import {SensorsAllInformationsViewRow} from '../../types/DbModel';
import Authentication from '../../../components/Infrastructure/Authentication/Authentication';
import {DiscoverModuleResponse} from './DiscoverModuleResponse';
import {SensorInfo} from 'components/Services/Sensors/SensorInfo';
import {IServerSideGetRowsRequest} from 'ag-grid-enterprise';
import {Sensor} from '../../../components/Common/Types/Sensor';
import {getFilterParams, getSortParms} from '../Helpers/AgGridReactSSRQueryBuilder';
import {RunInfo} from '../../types';
import {CalibrationState} from '../../../components/Calibration/Model/CalibrationState';
import {AlarmState} from '../../../common/constants';
import {SensorType} from '../../../components/Common/Types/SensorType';

export class SensorService {
	public static async DiscoverModule(serialNumber: string): Promise<DiscoverModuleResponse> {
		try {
			const response = await request({
				url: '/rpc/discover_module',
				method: 'POST',
				data: {serial_number: serialNumber},
				token: Authentication.getToken(),
			});
			return response.data;
		} catch (e) {
			console.error(e);
			return await Promise.reject(e);
		}
	}

	public static async DiscoverRun(sensorId: number): Promise<{
		AffectedSensors: {id: number; name: string}[];
		Configuration: object;
	}> {
		try {
			const response = await request({
				url: '/rpc/discover_run',
				method: 'POST',
				data: {s_id: sensorId},
				token: Authentication.getToken(),
			});
			return {AffectedSensors: response.data.affected_sensors, Configuration: response.data.configuration};
		} catch (e) {
			console.error(e);
			return await Promise.reject(e);
		}
	}

	public static async SetAlarmingEnabled(sensor: Sensor, alarmingEnabled: boolean): Promise<void> {
		try {
			await request({
				url: '/rpc/activate_alarming',
				method: 'POST',
				token: Authentication.getToken(),
				data: {
					sensors_id: sensor.Id,
					activate: alarmingEnabled,
				},
			});
			pubsub.publish(`${UI_EVENT.SENSOR_ALARMING_ENABLED_CHANGED}.${sensor.Id}`, {
				AlarmingEnabled: alarmingEnabled,
				SensorId: sensor.Id,
			});
		} catch (e) {
			console.error(e);
			return Promise.reject(e);
		}
	}

	public static async GetModuleSensors(serial_number: string): Promise<SensorInfo[]> {
		try {
			const response = await request({
				url: '/sensors_info?serial_number=eq.' + serial_number.toUpperCase(),
				method: 'GET',
				token: Authentication.getToken(),
			});
			return response.data;
		} catch (e) {
			return await Promise.reject(new Error('Failed to get module sensors'));
		}
	}

	public static async GetSensorInfo(sensorId: number): Promise<SensorInfo> {
		try {
			const response = await request({
				url: '/sensors_info?id=eq.' + sensorId,
				method: 'GET',
				token: Authentication.getToken(),
			});
			return response.data[0];
		} catch (e) {
			return await Promise.reject(new Error('Failed to get SensorInfo'));
		}
	}

	public static async GetSensorsFromGridInfo(params: IServerSideGetRowsRequest): Promise<{
		total: number;
		sensors: SensorInfo[];
	}> {
		let sortParms = getSortParms(params);
		let filterParams = getFilterParams(params);
		let total: number;

		if (params.groupKeys.length < params.rowGroupCols.length) {
			return {
				total: 0,
				sensors: [],
			};
		}

		const sensorRequestOptions: any = {
			url: `/sensors_info?limit=${params.endRow - params.startRow}&offset=${params.startRow}${sortParms}${filterParams}`,
			method: 'GET',
			token: Authentication.getToken(),
		};

		// Only set Prefer header if filterParams are set
		if (filterParams) {
			sensorRequestOptions.headers = {Prefer: 'count=exact'};
		}

		let statsPromise: Promise<any> | undefined;
		if (!filterParams) {
			statsPromise = request({
				url: '/sensor_stats',
				method: 'GET',
				token: Authentication.getToken(),
			});
		}

		const [res, stats] = await Promise.all([request(sensorRequestOptions), statsPromise ?? Promise.resolve(undefined)]);

		// If filterParams are set, use content-range header for total
		if (filterParams) {
			total = parseInt(res.headers['content-range'].split('/')[1], 10);
		} else if (stats) {
			total = stats.data.length > 0 ? stats.data[0].count : 0;
		}

		return {
			total,
			sensors: res.data,
		};
	}

	public static async GetGroupedSensors(params: IServerSideGetRowsRequest): Promise<SensorInfo[]> {
		// Only keep sort params that are in the grouping columns
		let groupColIds = params.rowGroupCols.map(col => col.id);
		let filteredSortModel = params.sortModel.filter(sort => groupColIds.includes(sort.colId));
		let sortParms = getSortParms({...params, sortModel: filteredSortModel});
		let filterParams = getFilterParams(params);

		const response = await request({
			url: `/sensors_overview?${sortParms}${filterParams}`,
			method: 'GET',
			token: Authentication.getToken(),
		});

		const result = await response.data;
		return result.map(sensor => {
			const sensorInfo: SensorInfo = {
				id: sensor.id,
				serial_number: sensor.serial_number,
				channel_id: sensor.channel_id,
				module_short_name: sensor.module_short_name,
				module_family: sensor.module_family,
				device_state: sensor.device_state,
				custom_id: sensor.custom_id,
				metadata_1: sensor.metadata_1,
				metadata_2: sensor.metadata_2,
				metadata_3: sensor.metadata_3,
				metadata_4: sensor.metadata_4,
				metadata_5: sensor.metadata_5,
				metadata_6: sensor.metadata_6,
				metadata_7: sensor.metadata_7,
				metadata_8: sensor.metadata_8,
				alarm_state: sensor.alarm_state ?? AlarmState.None,
				device_name: '',
				out_units_id: 0,
				state: '',
				module_prefix: '',
				module_name: '',
				last_measurement_tstamp: '',
				last_measurement_value: 0,
				last_measurement_tilt_angle: 0,
				last_measurement_status: 0,
				last_measurement_error: '',
				sensor_groups_array: [],
				run_count: 0,
				ts_start: '',
				ts_end: '',
				start_transit_date: '',
				is_expired: false,
				is_retired: false,
				is_muted: false,
				latch_active: false,
				latched_status: false,
				has_alarm: false,
				has_unacknowledged_alarm: false,
				has_limits: false,
				calibrationState: CalibrationState.Inactive,
				libero_g_state_description: '',
				sensor_type: SensorType.Temperature,
				sensor_background_color: '',
				signal_strength: 0,
				is_multi_run: false,
				has_warning: false,
				has_unacknowledged_warning: false,
				l1: 0,
				h1: 0,
				searchable_text: '',
				muting_enabled: false,
				start_multirun_enabled: false,
				mr_locked_configuration: false,
				logging_interval: 0,
			};
			return sensorInfo;
		});
	}

	public static async GetChannelIds(): Promise<number[]> {
		try {
			const response = await request({
				url: '/sensors_overview?select=channel_id',
				method: 'GET',
				token: Authentication.getToken(),
			});
			return [...new Set<number>(response.data.map((d: {channel_id: number}) => d.channel_id))];
		} catch (e) {
			return await Promise.reject(new Error('Failed to get channel ids'));
		}
	}

	public static async GetDeviceStates(): Promise<string[]> {
		try {
			const response = await request({
				url: '/sensors_overview?select=device_state',
				method: 'GET',
				token: Authentication.getToken(),
			});
			return [...new Set<string>(response.data.map((d: {device_state: string}) => d.device_state))];
		} catch (e) {
			return await Promise.reject(new Error('Failed to get device states'));
		}
	}

	public static async GetModuleShortNames(): Promise<string[]> {
		try {
			const response = await request({
				url: '/sensors_overview?select=module_short_name',
				method: 'GET',
				token: Authentication.getToken(),
			});
			return [...new Set<string>(response.data.map((d: {module_short_name: string}) => d.module_short_name))];
		} catch (e) {
			return await Promise.reject(new Error('Failed to get module short names'));
		}
	}

	public static async GetSensorFamilies(): Promise<string[]> {
		try {
			const response = await request({
				url: '/sensors_overview?select=module_family',
				method: 'GET',
				token: Authentication.getToken(),
			});
			return [...new Set<string>(response.data.map((d: {module_family: string}) => d.module_family))];
		} catch (e) {
			console.error(e);
			return await Promise.reject(new Error('Failed to get sensor families'));
		}
	}

	public static async AllInformation(sensorId: number): Promise<SensorsAllInformationsViewRow> {
		try {
			const response = await request({
				url: '/sensors_all_informations?id=eq.' + sensorId,
				method: 'GET',
				headers: {Accept: 'application/vnd.pgrst.object'},
				token: Authentication.getToken(),
			});
			return response.data;
		} catch (e) {
			return Promise.reject(e);
		}
	}

	public static async GetMultiUseRunInfo(sensorId: number): Promise<RunInfo[]> {
		try {
			const response = await request({
				url: '/rpc/get_run_info',
				method: 'POST',
				data: {s_id: sensorId},
				token: Authentication.getToken(),
			});
			return response.data.map(r => new RunInfo(r));
		} catch (e) {
			return Promise.reject(e);
		}
	}
}
